import { type MouseEvent, useEffect, useState } from "react";
import {
  Box,
  IconButton,
  Typography,
} from "@mui/material";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import { OrgManagementRow } from "../../utils/types";
import { validateDate } from "../../utils/utils";
import CommonDatePicker from "./CommonDatePicker";
import ClearIcon from "@mui/icons-material/Clear";

type DateCellEditorProps = {
  row: OrgManagementRow;
  field: "startDate" | "endDate";
  value?: string;
  canEdit?: boolean;
  onSave: (
    row: OrgManagementRow,
    field: "startDate" | "endDate",
    value: string
  ) => void;
};

const DATE_PICKER_EVENT = "org-management-date-picker-opened";

const DateCellEditor = ({
  row,
  field,
  value,
  canEdit,
  onSave,
}: DateCellEditorProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isHovered, setIsHovered] = useState(false);
  const open = Boolean(anchorEl);

  useEffect(() => {
    const closePicker = () => {
      setAnchorEl(null);
    };

    window.addEventListener(DATE_PICKER_EVENT, closePicker);

    return () => {
      window.removeEventListener(DATE_PICKER_EVENT, closePicker);
    };
  }, []);

  const handleOpen = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    if (!canEdit) {
      return;
    }
    window.dispatchEvent(new Event(DATE_PICKER_EVENT));

    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleClear = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    if (!canEdit) {
      return;
    }
    onSave(row, field, "");
    handleClose();
  };

  return (
    <>
      <Box
        className={
          canEdit
            ? "domain-management-chip domain-management-chip-dashed"
            : "domain-management-chip"
        }
        sx={{
          position: "relative",
          gap: 0.5,
          cursor: canEdit ? "pointer" : "default",
          backgroundColor: canEdit ? "#ffffff" : "transparent",
          width: "100%",
          maxWidth: "140px",
          ...(open && {
            backgroundColor: "#F6F1FF",
            border: "1px dashed #B892FF",
          }),
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={handleOpen}
      >
        <Typography className="domain-management-chip-label">
          {validateDate(value)}
        </Typography>
        {canEdit && (
        <>
        {field === "endDate" && value && (isHovered || open) ? (
          <IconButton
            size="small"
            onClick={handleClear}
            sx={{ width: 20, height: 20 }}
          >
            <ClearIcon sx={{ fontSize: 14, color: "#9AA0AF" }} />
          </IconButton>
        ) : (
          <IconButton size="small" onClick={handleOpen}>
            <CalendarTodayOutlinedIcon
              sx={{ fontSize: 16, color: "#9AA0AF" }}
            />
          </IconButton>
        )}
        </>
        )}
      </Box>

      {open && (
        <CommonDatePicker
          value={value ?? null}
          anchorEl={anchorEl}
          minDate={field === "endDate" ? row.startDate : null}
          saveAsDateOnly
          onClose={handleClose}
          onChange={(selectedDate) => {
            if (!selectedDate) return;

            onSave(row, field, selectedDate);
            handleClose();
          }}
        />
      )}
    </>
  );
};

export default DateCellEditor;
